import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, KeyRound, Mail, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiBlob } from "../lib/api";
import { rolesOf } from "../lib/roles";
import { Empty, Loading, PageHeader, Panel } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FirstPasswordDialog } from "../components/FirstPasswordDialog";

const dt = (s) => new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const InviteStatus = ({ inv, id }) => {
  if (!inv) return null;
  const expired = !inv.used_at && inv.expires_at < new Date().toISOString();
  return (
    <div className="mt-1.5 text-xs leading-snug min-w-[190px]" data-testid={`user-invite-${id}`}>
      <p className="text-slate-500">{inv.kind === "reset" ? "Link nuova password inviato il" : "Email inviata il"} {dt(inv.created_at)}</p>
      {inv.used_at ? <p className="text-emerald-700 font-medium" data-testid={`user-invite-used-${id}`}>Link usato il {dt(inv.used_at)}</p>
        : expired ? <p className="text-red-600 font-medium" data-testid={`user-invite-expired-${id}`}>Link scaduto: invia di nuovo</p>
        : <p className="text-amber-700 font-medium" data-testid={`user-invite-pending-${id}`}>Non ancora usato · scade il {dt(inv.expires_at)}</p>}
    </div>
  );
};

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => api("/admin/users").then((d) => setUsers(d.users || [])).catch(() => setUsers([])), []);
  const [invites, setInvites] = useState({});
  const loadInvites = useCallback(() => api("/web/invites").then((d) => setInvites(Object.fromEntries((d.invites || []).map((i) => [i.user_id, i])))).catch(() => {}), []);
  useEffect(() => { load(); loadInvites(); }, [load, loadInvites]);

  const exportXls = async () => {
    try {
      const res = await apiBlob("/admin/users/export/xlsx");
      const url = URL.createObjectURL(await res.blob());
      Object.assign(document.createElement("a"), { href: url, download: "anagrafica.xlsx" }).click();
      toast.success("Anagrafica esportata in XLS");
    } catch { toast.error("Errore durante l'export"); }
  };
  const resetLink = async (u) => {
    if (!window.confirm(`Inviare a ${u.nome} ${u.cognome} (${u.email}) un link per scegliere una nuova password?\n\nLa password attuale smetterà subito di funzionare, sia sulla web app sia sull'app mobile.`)) return;
    try { await api(`/web/reset-link/${u.id}`, { method: "POST" }); toast.success(`Link per la nuova password inviato a ${u.email}`); loadInvites(); }
    catch (e) { toast.error(e.message || "Email non inviata"); }
  };
  const launch = async (u) => {
    if (!window.confirm(`Inviare a ${u.nome} ${u.cognome} (${u.email}) l'email per attivare l'account?`)) return;
    try { await api(`/web/launch-email/${u.id}`, { method: "POST" }); toast.success(`Email di attivazione inviata a ${u.email}`); loadInvites(); }
    catch (e) { toast.error(e.message || "Email non inviata"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Eliminare l'utente ${u.nome} ${u.cognome}?`)) return;
    try { await api(`/admin/users/${u.id}`, { method: "DELETE" }); toast.success("Utente eliminato"); load(); } catch (e) { toast.error(e.message); }
  };

  const filtered = (users || []).filter((u) => `${u.nome} ${u.cognome} ${u.email} ${rolesOf(u).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <PageHeader subtitle="Pannello Direttore" title="Gestione Utenti" actions={<>
        <FirstPasswordDialog />
        <Button variant="outline" className="rounded-full" onClick={exportXls} data-testid="users-export-button"><Download className="w-4 h-4 mr-1" />Esporta anagrafica (XLS)</Button>
        <Button asChild className="rounded-full bg-[var(--teal)]" data-testid="user-new-button"><Link to="/admin/users/new"><Plus className="w-4 h-4 mr-1" />Nuovo utente</Link></Button></>} />
      <div className="relative mb-6 max-w-sm"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, email, mansione" className="pl-9 bg-white" data-testid="users-search-input" /></div>
      {!users ? <Loading /> : filtered.length === 0 ? <Empty title="Nessun utente" /> : (
        <Panel className="!p-0 overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b"><th className="p-4">Nome</th><th className="p-4">Mansioni</th><th className="p-4">Email</th><th className="p-4">Stato</th><th className="p-4"></th></tr></thead>
            <tbody>{filtered.map((u) => (
              <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="p-4 font-semibold text-[var(--navy)]">{u.cognome} {u.nome}{u.legge_104 && <span className="tag tag-amber ml-2">L.104</span>}</td>
                <td className="p-4 text-slate-600">{rolesOf(u).join(", ")}</td>
                <td className="p-4 text-slate-600">{u.email}</td>
                <td className="p-4">{u.is_active === false ? <span className="tag tag-slate">Disattivato</span> : u.has_logged_in ? <span className="tag tag-green">Attivo</span> : <span className="tag tag-amber">Mai entrato</span>}<InviteStatus inv={invites[u.id]} id={u.id} /></td>
                <td className="p-4"><div className="flex gap-1 justify-end">
                  <Button size="icon" variant="ghost" asChild data-testid={`user-edit-${u.id}`}><Link to={`/admin/users/${u.id}`}><Pencil className="w-4 h-4" /></Link></Button>
                  <Button size="icon" variant="ghost" onClick={() => launch(u)} disabled={u.has_logged_in || u.is_active === false} title={u.has_logged_in ? "Ha già effettuato l'accesso" : "Invia email di attivazione"} data-testid={`user-email-${u.id}`}><Mail className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => resetLink(u)} disabled={u.is_active === false} title="Invia link per una nuova password" data-testid={`user-reset-link-${u.id}`}><KeyRound className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-[var(--red)]" onClick={() => del(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div></td>
              </tr>))}
            </tbody>
          </table>
        </Panel>)}
    </div>
  );
}
